import type { PlotSeries } from "@/shared/state/visualization";

import { Timer, useTimerState } from "../../timer";

import type { SeriesAlignment } from "./alignment";
import { absolutePlayheadPixel, playheadPositions } from "./playheadPositions";
import type { Extents } from "./PlotRenderer";
import styles from "./TimelineIndicator.module.css";

interface TimelineIndicatorProps {
  chartExtents: Extents | null;
  timer: Timer;
  series: PlotSeries[];
  /**
   * How each series plots in aligned mode; `undefined` when the panel plots
   * absolute time.
   */
  alignment: SeriesAlignment | undefined;
  showPlayhead: boolean;
}

export function TimelineIndicator(props: TimelineIndicatorProps) {
  const { chartExtents, timer, series, alignment, showPlayhead } = props;
  const { timeNs } = useTimerState(timer);

  if (!showPlayhead || chartExtents === null || timer.duration === 0) {
    return null;
  }

  const {
    pixel: { top, height },
  } = chartExtents;

  if (alignment !== undefined) {
    const positions = playheadPositions({
      series,
      alignment,
      timeNs,
      viewportDomain: chartExtents.data.viewportDomain,
      pixel: chartExtents.pixel,
    });
    return (
      <>
        {positions.map((position) => (
          <IndicatorLine
            key={position.id}
            top={top}
            height={height}
            left={position.left}
            color={position.color}
          />
        ))}
      </>
    );
  }

  const left = absolutePlayheadPixel(timeNs, chartExtents);
  if (left === null) {
    return null;
  }

  return <IndicatorLine top={top} height={height} left={left} />;
}

interface IndicatorLineProps {
  top: number;
  height: number;
  left: number;
  /** Tints the line; omit it to keep the stylesheet's color. */
  color?: string;
}

function IndicatorLine({ top, height, left, color }: IndicatorLineProps) {
  return (
    <div
      className={styles.indicator}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        left: `${left}px`,
        backgroundColor: color,
      }}
    />
  );
}
